import { compass } from '../core/math';
import { INTRO_H, INTRO_W } from '../render/intro';
import {
  CUSTOM_DEFAULTS,
  CUSTOM_FIELDS,
  DIFFICULTIES,
  makeCustom,
  type CustomOptions,
  type Difficulty,
} from '../sim/difficulty';
import {
  EState,
  TACTICS,
  type Engine,
  type Objective,
  type Tactic,
  engineStatus,
  namedObjectives,
  tacticRefusal,
} from '../sim/engine';
import { heliPrompt } from '../sim/heli';
import { GAMES, SHOW_SITE_LINKS, SITE, copyrightHtml, extLink, siteUrl } from '../site';
import { CELL, CellState, GRID_H, GRID_W, T, idx } from '../sim/types';
import { nextShift, nextShiftIn } from '../sim/wind';
import type { World } from '../sim/world';

const el = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

const mmss = (s: number): string =>
  `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const pct = (v: number): string => `${Math.round(v * 100)}%`;
const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');

/** Which entry in GAMES this build is, so the footer does not link to itself. */
const THIS_GAME = 'fire-rescue';

type TabId = 'incident' | 'fire' | 'units' | 'aircraft' | 'log' | 'controls';

const TABS: { id: TabId; name: string }[] = [
  { id: 'incident', name: 'INCIDENT' },
  { id: 'fire', name: 'FIRE & WIND' },
  { id: 'units', name: 'GROUND UNITS' },
  { id: 'aircraft', name: 'AIRCRAFT' },
  { id: 'log', name: 'LOG' },
  { id: 'controls', name: 'CONTROLS' },
];

export class Hud {
  private readonly o = el('overlay');
  private readonly dash = el('dash');

  /** The tactic waiting for an objective. */
  armed: Tactic | null = null;
  tab: TabId = 'incident';

  onSelect: (id: number) => void = () => {};
  onHold: (id: number) => void = () => {};
  /** Issue the armed tactic against a named objective. */
  onOrder: (id: number, tactic: Tactic, obj: Objective) => void = () => {};
  onResume: () => void = () => {};

  introCtx: CanvasRenderingContext2D | null = null;

  private tabsBuilt = false;
  private dashSig = '';
  private flashUntil = 0;
  private flashSeen = 0;

  // -------------------------------------------------------------------------
  // Always-on corner panels
  // -------------------------------------------------------------------------

  update(w: World, speed: number, muted: boolean): void {
    const s = w.stats;
    const atRisk = s.home + s.fleeing + s.inCar + s.waiting + s.aboard;

    el('clock').textContent = mmss(w.elapsed);
    el('tierName').textContent = w.diff.name;
    el('speedBadge').textContent = speed === 0 ? 'PAUSED' : `${speed}×${muted ? ' MUTE' : ''}`;
    el('cRisk').textContent = String(atRisk);
    el('cSafe').textContent = String(s.safe + s.rescued);
    el('cLost').textContent = String(s.lost);
    el<HTMLElement>('savedBar').style.width = `${s.total ? ((s.safe + s.rescued) / s.total) * 100 : 0}%`;
    el('windNow').textContent = `${Math.round(w.wind.speed * 60)} km/h ${compass(w.wind.dir)}`;
    el('fireNow').textContent = `${s.burning} cells · ${pct(s.burntFrac)} lost`;

    const h = w.heli;
    el('hMode').textContent =
      h.swapping > 0 ? `changing ${h.swapping.toFixed(1)}s` : h.mode === 'bucket' ? 'BUCKET' : 'SEATS';
    el('hLoadLabel').textContent = h.mode === 'bucket' ? 'Water' : 'Aboard';
    el('hLoad').textContent =
      h.mode === 'bucket' ? `${Math.round(h.water * w.diff.tankLitres)} l` : `${h.aboard.length}/${w.diff.seats}`;
    el<HTMLElement>('waterBar').style.width =
      h.mode === 'bucket' ? `${h.water * 100}%` : `${(h.aboard.length / w.diff.seats) * 100}%`;
    el('hFuel').textContent = pct(h.fuel);
    el<HTMLElement>('fuelBar').style.width = `${h.fuel * 100}%`;

    const idle = w.engines.filter((e) => e.state === EState.Staging && !e.order).length;
    const live = w.engines.filter((e) => e.state !== EState.Responding && e.state !== EState.Lost).length;
    const uSum = el('uSummary');
    uSum.textContent = idle > 0 ? `${idle} AWAITING ORDERS` : `${live} deployed`;
    uSum.className = idle > 0 ? 'v warn' : 'v';

    const cut = w.roads.filter((r) => r.cut !== null).length;
    const rSum = el('rSummary');
    rSum.textContent = cut === 0 ? 'both open' : cut === w.roads.length ? 'BOTH CUT' : '1 CUT';
    rSum.className = cut === 0 ? 'v good' : cut === w.roads.length ? 'v bad' : 'v warn';

    el('prompt').textContent = heliPrompt(w) ?? '';

    // Newest radio traffic, briefly, in the middle of the screen.
    const last = w.messages[w.messages.length - 1];
    const flash = el('flash');
    if (last && w.messages.length !== this.flashSeen) {
      this.flashSeen = w.messages.length;
      this.flashUntil = w.elapsed + 5;
      flash.textContent = last.text;
      flash.className = last.kind;
    }
    if (w.elapsed > this.flashUntil) flash.textContent = '';
  }

  // -------------------------------------------------------------------------
  // Control dashboard
  // -------------------------------------------------------------------------

  showDash(on: boolean): void {
    this.dash.classList.toggle('on', on);
    if (on && !this.tabsBuilt) this.buildTabs();
  }

  get dashOpen(): boolean {
    return this.dash.classList.contains('on');
  }

  private buildTabs(): void {
    this.tabsBuilt = true;
    const tabs = el('dashTabs');
    tabs.innerHTML = TABS.map(
      (t) =>
        `<button class="tab" data-tab="${t.id}"><span>${t.name}</span><span class="badge" data-badge="${t.id}" style="display:none"></span></button>`,
    ).join('');
    tabs.querySelectorAll<HTMLElement>('[data-tab]').forEach((b) =>
      b.addEventListener('click', () => {
        this.tab = b.dataset.tab as TabId;
        this.dashSig = '';
      }),
    );
    el('dashPanes').innerHTML = TABS.map((t) => `<div class="pane" data-pane="${t.id}"></div>`).join('');
    el('dashResume').addEventListener('click', () => this.onResume());
  }

  /**
   * A thumbnail of the mountain: terrain, fire, villages, and every unit with a
   * line to whatever it has been sent to. Seeing where your engines are and
   * where they are pointed beats reading it in a list.
   */
  private drawMiniMap(w: World, canvas: HTMLCanvasElement): void {
    const g = canvas.getContext('2d');
    if (!g) return;
    const S = 2; // pixels per cell
    canvas.width = GRID_W * S;
    canvas.height = GRID_H * S;
    g.fillStyle = '#05050c';
    g.fillRect(0, 0, canvas.width, canvas.height);

    for (let y = 0; y < GRID_H; y++) {
      for (let x = 0; x < GRID_W; x++) {
        const i = idx(x, y);
        const st = w.grid.state[i];
        let c: string;
        if (st === CellState.Burning) c = '#ff6a00';
        else if (st === CellState.Burnt) c = '#1e1e26';
        else
          switch (w.grid.terrain[i]) {
            case T.Sky: continue;
            case T.Water: c = '#1030a0'; break;
            case T.Acequia: c = '#14868f'; break;
            case T.Road: c = '#7a7a8e'; break;
            case T.Rock: c = '#3f3f52'; break;
            case T.Pine: c = '#00451a'; break;
            case T.Scrub: c = '#4a561a'; break;
            case T.Olive: c = '#666c30'; break;
            case T.Grass: c = '#8a8a2c'; break;
            // Warm cream, never red. Red on this map means a fire engine, and
            // eight villages of red roofs read as a fleet of trucks that has
            // not arrived yet.
            case T.Building: c = '#e8d6a8'; break;
            case T.Helipad: case T.Base: c = '#7f7fb4'; break;
            default: c = '#2a2a36';
          }
        g.fillStyle = c;
        g.fillRect(x * S, y * S, S, S);
      }
    }

    // Villages, so the objective names have somewhere to point.
    g.font = '9px monospace';
    for (const v of w.mtn.villages) {
      g.fillStyle = '#ffffff';
      g.fillRect(v.cx * S - 2, v.cy * S - 2, 5, 5);
      g.fillStyle = '#000000';
      g.fillRect(v.cx * S - 1, v.cy * S - 1, 3, 3);
    }

    // The aircraft.
    g.fillStyle = '#ffff00';
    g.fillRect(Math.round((w.heli.x / CELL) * S) - 3, Math.round((w.heli.y / CELL) * S) - 2, 6, 4);

    // Units, and a line to whatever each has been sent to.
    for (const e of w.engines) {
      if (e.state === EState.Responding) continue;
      const road = w.roads[e.roadId];
      const c = road.cells[Math.max(0, Math.min(road.cells.length - 1, Math.round(e.s)))];
      const ex = c.x * S;
      const ey = c.y * S;
      if (e.order) {
        const o = e.order.objective;
        g.strokeStyle = w.selected === e.id ? '#00ffff' : '#8a8aa4';
        g.setLineDash([3, 3]);
        g.beginPath();
        g.moveTo(ex + 1, ey + 1);
        g.lineTo(o.x * S + 1, o.y * S + 1);
        g.stroke();
        g.setLineDash([]);
        g.fillStyle = w.selected === e.id ? '#00ffff' : '#8a8aa4';
        g.fillRect(o.x * S - 3, o.y * S - 3, 7, 7);
      }
      g.fillStyle = e.state === EState.Lost ? '#4a4a4a' : '#ff2020';
      g.fillRect(ex - 3, ey - 3, 7, 7);
      if (w.selected === e.id) {
        g.strokeStyle = '#00ffff';
        g.strokeRect(ex - 5.5, ey - 5.5, 12, 12);
      }
      if (!e.order && e.state !== EState.Lost) {
        g.fillStyle = '#ffff00';
        g.fillRect(ex - 1, ey - 10, 3, 5);
      }
    }
  }

  /** Rebuild the dashboard contents. Cheap enough to run each frame while paused. */
  updateDash(w: World): void {
    if (!this.dashOpen) return;
    if (!this.tabsBuilt) this.buildTabs();

    const s = w.stats;
    const atRisk = s.home + s.fleeing + s.inCar + s.waiting + s.aboard;
    el('dashSub').textContent = `${w.diff.name} · ${w.diff.rank} · seed ${w.seed}`;
    el('dashClock').textContent = mmss(w.elapsed);

    // Above the early return below, so the heading can never lag behind the tab.
    el('dashTabName').textContent = TABS.find((t) => t.id === this.tab)?.name ?? '';

    const idle = w.engines.filter((e) => e.state === EState.Staging && !e.order).length;
    el('dashTabs')
      .querySelectorAll<HTMLElement>('[data-tab]')
      .forEach((b) => b.classList.toggle('on', b.dataset.tab === this.tab));
    const badge = el('dashTabs').querySelector<HTMLElement>('[data-badge="units"]');
    if (badge) {
      badge.style.display = idle > 0 ? 'inline-block' : 'none';
      badge.textContent = String(idle);
    }

    // Only the visible pane needs rebuilding, and only when something changed.
    const sig = [
      this.tab,
      this.armed,
      w.selected,
      Math.floor(w.elapsed),
      w.messages.length,
      w.engines.map((e) => `${e.state}${e.warned ? 'W' : ''}${Math.round(e.water * 10)}${Math.round(e.crew * 10)}`).join(''),
    ].join('|');
    if (sig === this.dashSig) return;
    this.dashSig = sig;

    el('dashPanes')
      .querySelectorAll<HTMLElement>('[data-pane]')
      .forEach((p) => p.classList.toggle('on', p.dataset.pane === this.tab));
    const pane = el('dashPanes').querySelector<HTMLElement>(`[data-pane="${this.tab}"]`);
    if (!pane) return;

    switch (this.tab) {
      case 'incident':
        pane.innerHTML = this.paneIncident(w, atRisk);
        break;
      case 'fire':
        pane.innerHTML = this.paneFire(w);
        break;
      case 'units':
        pane.innerHTML = this.paneUnits(w);
        this.wireUnits(w, pane);
        break;
      case 'aircraft':
        pane.innerHTML = this.paneAircraft(w);
        break;
      case 'log':
        pane.innerHTML = this.paneLog(w);
        break;
      case 'controls':
        pane.innerHTML = this.paneControls();
        break;
    }
  }

  private paneIncident(w: World, atRisk: number): string {
    const s = w.stats;
    return `
      <h2>Objective</h2>
      <div class="huge">${atRisk > 0 ? `${atRisk} still on the mountain` : 'Everybody accounted for'}</div>
      <p class="note">
        There is no quota. The job is all ${s.total} of them. Your grade then counts how much of the
        mountain and how many buildings you kept as well.
      </p>
      <h2>What to do now</h2>
      <ul class="tips">
        ${w.tips().map((t) => `<li class="${t.urgent ? 'urgent' : ''}">${esc(t.text)}</li>`).join('')}
      </ul>
      <h2>Where everybody is</h2>
      ${this.peopleBar(s)}
      <h2>On the mountain</h2>
      <div class="cols">
        <div class="stat"><span>Still at risk</span><span>${atRisk} of ${s.total}</span></div>
        <div class="stat"><span>Buildings standing</span><span>${s.housesTotal - s.housesLost} of ${s.housesTotal}</span></div>
        ${s.crewLost > 0 ? `<div class="stat bad"><span>Firefighters lost</span><span>${s.crewLost}</span></div>` : ''}
      </div>
      <p class="note">
        ${w.evacOrdered
          ? 'General evacuation has been ordered.'
          : 'You have not ordered the general evacuation yet (V). Order it too early and the tight bends on the road jam with cars. Order it too late and people are still indoors when the fire arrives.'}
      </p>`;
  }

  /** One bar showing every civilian at once, by what they are doing. */
  private peopleBar(s: World['stats']): string {
    const parts: [string, number, string][] = [
      ['#6a6a84', s.home, 'indoors'],
      ['#ffff00', s.fleeing, 'on foot'],
      ['#ff00ff', s.inCar, 'in cars'],
      ['#00ffff', s.waiting, 'at the assembly point'],
      ['#ff8700', s.aboard, 'aboard'],
      ['#00ff00', s.safe + s.rescued, 'off the mountain'],
      ['#ff0000', s.lost, 'lost'],
    ];
    const total = Math.max(1, s.total);
    return `
      <div class="peoplebar">
        ${parts
          .filter(([, n]) => n > 0)
          .map(([c, n]) => `<i style="background:${c};width:${(n / total) * 100}%" title="${n}"></i>`)
          .join('')}
      </div>
      <div class="pkey">
        ${parts
          .map(([c, n, label]) => `<span><i style="background:${c}"></i>${label} <b>${n}</b></span>`)
          .join('')}
      </div>`;
  }

  private paneFire(w: World): string {
    const s = w.stats;
    const inSec = nextShiftIn(w.wind, w.elapsed);
    const ns = nextShift(w.wind);
    return `
      <h2>Fire</h2>
      <div class="cols">
        <div>
          <div class="stat"><span>Active front</span><span>${s.burning} cells</span></div>
          <div class="stat"><span>Burnt</span><span>${s.hectares.toFixed(1)} ha</span></div>
        </div>
        <div>
          <div class="stat"><span>Mountain lost</span><span>${pct(s.burntFrac)}</span></div>
          <div class="stat"><span>Grade A allows up to</span><span>${pct(w.diff.maxBurnA)}</span></div>
        </div>
      </div>
      <h2>Wind</h2>
      <div style="display:flex;gap:26px;align-items:center;flex-wrap:wrap">
        <div class="compass">
          <b>uphill ↑</b>
          <i style="transform:rotate(${(w.wind.dir * 180) / Math.PI + 90}deg)"></i>
          <u>${compass(w.wind.dir)}</u>
        </div>
        <div style="flex:1;min-width:260px">
          <div class="stat"><span>Speed</span><span>${Math.round(w.wind.speed * 60)} km/h</span></div>
          <div class="meter m-fuel"><i style="width:${Math.min(100, w.wind.speed * 100)}%"></i></div>
          <div class="stat warn"><span>Next shift</span><span>${
            inSec === null || !ns ? 'settled' : `${Math.max(0, Math.round(inSec))}s → ${compass(ns.dir)}`
          }</span></div>
          <div class="stat"><span>Air temperature</span><span>${w.diff.airTempC}°C</span></div>
        </div>
      </div>
      <p class="note">
        Fire climbs roughly three times faster than it burns downhill, and the wind decides which side of it
        spreads fastest. The forecast is published in advance on purpose. Put water where the fire is
        going, not where it is now.
      </p>
      <h2>Roads</h2>
      ${w.roads
        .map(
          (r) => `<div class="stat ${r.cut !== null ? 'bad' : r.jam > 0.5 ? 'warn' : 'good'}">
            <span>${esc(r.name)}</span>
            <span>${r.cut !== null ? 'CUT BY FIRE' : r.jam > 0.5 ? 'JAMMED' : 'OPEN'} · ${r.through} cars out</span>
          </div>`,
        )
        .join('')}`;
  }

  /** What each unit's dropdowns are currently set to, kept across redraws. */
  private pending = new Map<number, { tactic: Tactic; objKey: string }>();

  private paneUnits(w: World): string {
    if (w.engines.length === 0) return '<p class="note">No ground units on this incident.</p>';
    const objectives = namedObjectives(w);

    const cards = w.engines
      .map((e) => {
        const gone = e.state === EState.Lost;
        const live = !gone && e.state !== EState.Responding;
        const chosen = w.selected === e.id;
        const p = this.pending.get(e.id) ?? { tactic: 'protect' as Tactic, objKey: objectives[0]?.key ?? '' };

        const controls = !live
          ? `<p class="note">${gone ? 'Lost.' : `On its way. Arrives in ${Math.ceil(e.eta)}s.`}</p>`
          : `<div class="vrow">
               <label>TACTIC</label>
               <select class="vsel" data-tacsel="${e.id}">
                 ${TACTICS.map(
                   (t) => `<option value="${t.key}"${p.tactic === t.key ? ' selected' : ''}>${t.name}</option>`,
                 ).join('')}
               </select>
               <label>SEND TO</label>
               <select class="vsel" data-objsel="${e.id}" ${p.tactic === 'withdraw' ? 'disabled' : ''}>
                 ${objectives
                   .map((o) => {
                     const why = p.tactic === 'withdraw' ? null : tacticRefusal(w, p.tactic, o.obj);
                     return `<option value="${o.key}"${p.objKey === o.key ? ' selected' : ''}${why ? ' disabled' : ''}>${esc(o.label)}${why ? ' (not possible)' : ''}</option>`;
                   })
                   .join('')}
               </select>
               <button class="cmd" data-send="${e.id}">SEND</button>
               ${e.warned ? `<button class="cmd danger" data-hold="${e.id}">HOLD POSITION</button>` : ''}
               <button class="cmd" data-pick="${e.id}">${chosen ? 'SHOWN' : 'SHOW'}</button>
             </div>`;

        return `
        <div class="unitcard${chosen ? ' sel' : ''}${gone ? ' gone' : ''}">
          <div class="hd">
            <span class="nm">${e.name}</span>
            <span class="st ${e.warned ? 'bad' : ''}">${esc(engineStatus(w, e))}</span>
          </div>
          ${e.note ? `<div class="note" style="color:var(--yellowb);margin:2px 0 0">${esc(e.note)}</div>` : ''}
          ${
            live
              ? `<div class="bars">
                   <div><div class="lbl">WATER ${Math.round(e.water * 100)}%</div>
                     <div class="meter m-water"><i style="width:${e.water * 100}%"></i></div></div>
                   <div><div class="lbl">CREW ${Math.round(e.crew * 100)}%</div>
                     <div class="meter m-saved"><i style="width:${e.crew * 100}%"></i></div></div>
                 </div>`
              : ''
          }
          ${controls}
        </div>`;
      })
      .join('');

    return `
      <canvas class="minimap" id="unitsMap"></canvas>
      <div class="legend">
        <span><i style="background:#ff2020"></i>fire engine</span>
        <span><i style="background:#ffff00"></i>helicopter, and engines with no orders</span>
        <span><i style="background:#00ffff"></i>where a unit has been sent</span>
        <span><i style="background:#ff6a00"></i>fire</span>
        <span><i style="background:#e8d6a8"></i>houses</span>
        <span><i style="background:#ffffff"></i>village</span>
        <span><i style="background:#7f7fb4"></i>landing pad</span>
      </div>
      ${cards}
      <p class="note">
        Each fire engine takes its own tactic and its own destination. Set both, press SEND, and the
        crew starts moving the moment you resume. The dashed line on the map above shows where it
        is heading.
      </p>`;
  }

  private wireUnits(w: World, pane: HTMLElement): void {
    const map = pane.querySelector<HTMLCanvasElement>('#unitsMap');
    if (map) this.drawMiniMap(w, map);

    const objectives = namedObjectives(w);
    const get = (id: number) =>
      this.pending.get(id) ?? { tactic: 'protect' as Tactic, objKey: objectives[0]?.key ?? '' };

    pane.querySelectorAll<HTMLSelectElement>('[data-tacsel]').forEach((s) =>
      s.addEventListener('change', () => {
        const id = Number(s.dataset.tacsel);
        this.pending.set(id, { ...get(id), tactic: s.value as Tactic });
        this.dashSig = ''; // the objective list depends on the tactic
      }),
    );
    pane.querySelectorAll<HTMLSelectElement>('[data-objsel]').forEach((s) =>
      s.addEventListener('change', () => {
        const id = Number(s.dataset.objsel);
        this.pending.set(id, { ...get(id), objKey: s.value });
      }),
    );
    pane.querySelectorAll<HTMLElement>('[data-send]').forEach((b) =>
      b.addEventListener('click', () => {
        const id = Number(b.dataset.send);
        const p = get(id);
        if (p.tactic === 'withdraw') {
          this.onOrder(id, 'withdraw', objectives[0]?.obj ?? ({} as Objective));
        } else {
          const found = objectives.find((o) => o.key === p.objKey);
          if (found) this.onOrder(id, p.tactic, found.obj);
        }
        this.dashSig = '';
      }),
    );
    pane.querySelectorAll<HTMLElement>('[data-hold]').forEach((b) =>
      b.addEventListener('click', () => this.onHold(Number(b.dataset.hold))),
    );
    pane.querySelectorAll<HTMLElement>('[data-pick]').forEach((b) =>
      b.addEventListener('click', () => {
        this.onSelect(Number(b.dataset.pick));
        this.dashSig = '';
      }),
    );
  }

  private paneAircraft(w: World): string {
    const h = w.heli;
    const d = w.diff;
    return `
      <h2>Configuration</h2>
      <div class="huge">${h.mode === 'bucket' ? 'BUCKET' : `${d.seats} SEATS`}</div>
      <p class="note">
        ${h.mode === 'bucket'
          ? `Fitted for firefighting. You cannot carry anybody until you land on the base pad and press B, which takes ${d.swapTime}s.`
          : `Fitted for rescue. You cannot put water on anything until you swap the bucket back on at base.`}
      </p>
      <h2>State</h2>
      <div class="cols">
        <div>
          <div class="stat"><span>Water</span><span>${Math.round(h.water * d.tankLitres)} of ${d.tankLitres} l</span></div>
          <div class="stat"><span>Passengers</span><span>${h.aboard.length} of ${d.seats}</span></div>
          <div class="stat ${h.fuel < 0.25 ? 'bad' : ''}"><span>Fuel</span><span>${pct(h.fuel)}</span></div>
        </div>
        <div>
          <div class="stat"><span>Drop covers</span><span>~${Math.round(Math.PI * d.dropRadius * d.dropRadius)} cells</span></div>
          <div class="stat"><span>Scoop takes</span><span>${d.scoopTime}s</span></div>
          <div class="stat"><span>Waiting at the pad</span><span>${w.stats.waiting}</span></div>
        </div>
      </div>
      <p class="note">
        The dashed ring on the map is where the water will actually land. It already allows for the wind
        and for your own speed. Wet ground stays wet for about a minute, so a wet strip laid across the
        path of the fire will stop it.
      </p>`;
  }

  private paneLog(w: World): string {
    return `<div id="logList">${w.messages
      .slice(-40)
      .reverse()
      .map((m) => `<div class="${m.kind}"><b>${mmss(m.t)}</b>${esc(m.text)}</div>`)
      .join('')}</div>`;
  }

  private paneControls(): string {
    return `
      <h2>Flying</h2>
      <dl class="keys">
        <dt>W A S D / arrows</dt><dd>fly the helicopter</dd>
        <dt>E (hold)</dt><dd>scoop over the reservoir · land and load at the white H · unload and refuel at the base pad</dd>
        <dt>SPACE</dt><dd>release the water</dd>
        <dt>B</dt><dd>swap bucket ↔ seats, on the base pad only</dd>
        <dt>V</dt><dd>order the general evacuation</dd>
      </dl>
      <h2>Commanding</h2>
      <dl class="keys">
        <dt>P</dt><dd>open and close this dashboard. The game is paused while it is open, so you can take your time.</dd>
        <dt>GROUND UNITS tab</dt><dd>select a unit, choose a tactic, pick where to send it</dd>
        <dt>click the map</dt><dd>while playing, once a tactic is chosen, you can also click a target directly</dd>
        <dt>TAB</dt><dd>cycle through the units</dd>
      </dl>
      <h2>Everything else</h2>
      <dl class="keys">
        <dt>- and =</dt><dd>slow down and speed up</dd>
        <dt>M</dt><dd>mute</dd>
        <dt>R</dt><dd>restart this incident</dd>
        <dt>ESC</dt><dd>back to the incident list</dd>
      </dl>
      <h2>Reading the mountain</h2>
      <p class="note">
        A red glow on the ground means that the plants there are heating up and are about to catch fire.
        That is where the fire goes next. Blue ground is wet and will not light. Black ground has
        already burnt, so it is safe to stand on. The thin cyan lines across the slope are acequias,
        the old irrigation channels. Fire will not cross them, and the engines draw water from them.
      </p>`;
  }

  // -------------------------------------------------------------------------
  // Menu and after-action
  // -------------------------------------------------------------------------

  hide(): void {
    this.o.classList.remove('on');
    this.o.innerHTML = '';
    this.introCtx = null;
  }

  private show(html: string, wide = false): HTMLElement {
    this.introCtx = null;
    this.o.innerHTML = `<div class="card${wide ? ' wide' : ''}">${html}</div>`;
    this.o.classList.add('on');
    return this.o.querySelector('.card') as HTMLElement;
  }

  /** The player's own settings, remembered between visits to the menu. */
  private custom: CustomOptions = { ...CUSTOM_DEFAULTS };

  menu(onPick: (d: Difficulty, seed: number) => void): void {
    const cards = DIFFICULTIES.map(
      (d) => `
      <button class="diff" data-key="${d.key}">
        <h3>${d.name}</h3>
        <div class="rank">${d.rank}</div>
        <div class="mini"><b>${d.airTempC}°C</b>, wind <b>${Math.round(d.windBase * 60)}</b> km/h<br />
          <b>${d.residents}</b> people, <b>${d.ignitions}</b> fire${d.ignitions === 1 ? '' : 's'}</div>
        <div class="pop">
          <div class="blurb">${d.blurb}</div>
          <div class="stats">
            Air temperature <b>${d.airTempC}°C</b> &middot; wind <b>${Math.round(d.windBase * 60)} km/h</b><br />
            People on the mountain <b>${d.residents}</b> &middot; fires started <b>${d.ignitions}</b><br />
            Helicopter bucket <b>${d.tankLitres} litres</b> &middot; seats <b>${d.seats}</b><br />
            Fire engines <b>${d.engines}</b>, first one arrives after <b>${Math.round(d.engineFirst)}s</b>
          </div>
        </div>
      </button>`,
    ).join('');

    const card = this.show(
      `
      <canvas id="introart" width="${INTRO_W}" height="${INTRO_H}"></canvas>
      <p class="lede">
        Late August in the Sierra Nevada, the mountain range above the town of Lanjar&oacute;n in
        southern Spain. A fire has started in the barranco, the dry stony gully below the farm
        terraces. The only water you can refill from is the Embalse de B&eacute;znar, a reservoir
        down in the valley. You fly the one helicopter, and it can be fitted either with a water
        bucket or with passenger seats. One road serves the mountain, the A-348: west to
        B&eacute;znar, east to &Oacute;rgiva. When the fire closes it, the only way out is uphill on
        foot. <b>Everybody gets off this mountain: that is your job.</b>
      </p>
      <p class="hint">Hover over an incident to read what you are walking into. Click to start it.</p>
      <div class="grid5">
        ${cards}
        <button class="diff custom" data-key="__custom">
          <h3>A medida</h3>
          <div class="rank">Custom incident</div>
          <div class="mini" style="color:var(--cyanb)">you set<br />all of it &rsaquo;</div>
          <div class="pop">
            <div class="blurb">
              Build the incident yourself. Ten settings: the temperature, the wind, how many people
              are on the mountain, how many separate fires start, and what equipment you get to
              fight them with.
            </div>
            <div class="stats">Everything above, on a slider.</div>
          </div>
        </button>
      </div>
      ${this.siteFooter()}
    `,
      true,
    );

    card.querySelectorAll<HTMLButtonElement>('.diff').forEach((b) => {
      // CSS :hover opens the panel on its own. The class is here as well so that
      // keyboard focus and a touch screen open it too, and so it can be tested.
      const open = (on: boolean) => b.classList.toggle('open', on);
      b.addEventListener('mouseenter', () => open(true));
      b.addEventListener('mouseleave', () => open(false));
      b.addEventListener('focus', () => open(true));
      b.addEventListener('blur', () => open(false));
      b.addEventListener('click', () => {
        if (b.dataset.key === '__custom') {
          this.customMenu(onPick);
          return;
        }
        const d = DIFFICULTIES.find((x) => x.key === b.dataset.key)!;
        onPick(d, (Math.random() * 0xffffffff) >>> 0);
      });
    });
    this.introCtx = card.querySelector<HTMLCanvasElement>('#introart')?.getContext('2d') ?? null;
  }

  /**
   * The one line that says who made this and where the rest of it lives.
   *
   * It sits on the incident list and nowhere else. There is no save, so a link
   * anywhere a player might hit it mid-incident would throw their evening away.
   * It also has to be here rather than only on the website, because an embedded
   * copy of the game on somebody else's page carries no other clue where it
   * came from.
   */
  private siteFooter(): string {
    if (!SHOW_SITE_LINKS) return '';
    const others = GAMES.filter((g) => g.slug !== THIS_GAME);
    const more = others.length
      ? ` &middot; also from us: ${others.map((g) => extLink(siteUrl(g.slug), esc(g.title))).join(' &middot; ')}`
      : '';
    return `<p class="foot">${extLink(SITE.url, SITE.name.toUpperCase(), 'ext brand')} &middot; ${copyrightHtml()}${more}</p>`;
  }

  /** The custom incident set-up: every option the game has, on a slider. */
  private customMenu(onPick: (d: Difficulty, seed: number) => void): void {
    const fields = CUSTOM_FIELDS.map(
      (f) => `
      <div class="f">
        <div class="top">
          <span class="nm">${f.label}</span>
          <span class="val" data-val="${f.key}">${this.custom[f.key]}${f.unit}</span>
        </div>
        <input type="range" data-set="${f.key}" min="${f.min}" max="${f.max}" step="${f.step}"
               value="${this.custom[f.key]}" />
        <div class="hlp">${f.help}</div>
      </div>`,
    ).join('');

    const card = this.show(`
      <h1>A MEDIDA</h1>
      <p class="lede">
        Your own incident on the same mountain. Everybody still has to get off it. You are choosing
        how hard that will be.
      </p>
      <div class="setup">${fields}</div>
      <button class="btn" id="startCustom">START THIS INCIDENT</button>
      <button class="btn" id="backToList">BACK</button>
      <button class="btn" id="resetCustom">RESET</button>
    `);

    const sync = () => {
      for (const f of CUSTOM_FIELDS) {
        const out = card.querySelector<HTMLElement>(`[data-val="${f.key}"]`);
        if (out) out.textContent = `${this.custom[f.key]}${f.unit}`;
      }
    };
    card.querySelectorAll<HTMLInputElement>('[data-set]').forEach((input) =>
      input.addEventListener('input', () => {
        this.custom[input.dataset.set as keyof CustomOptions] = Number(input.value);
        sync();
      }),
    );
    card.querySelector('#startCustom')!.addEventListener('click', () => {
      onPick(makeCustom(this.custom), (Math.random() * 0xffffffff) >>> 0);
    });
    card.querySelector('#backToList')!.addEventListener('click', () => this.menu(onPick));
    card.querySelector('#resetCustom')!.addEventListener('click', () => {
      this.custom = { ...CUSTOM_DEFAULTS };
      this.customMenu(onPick);
    });
  }

  result(w: World, onAgain: () => void, onMenu: () => void): void {
    const r = w.result!;
    const commentary = r.pass
      ? 'Everybody got off the mountain. That is the job done.'
      : `${r.lost} did not get off the mountain.`;

    const card = this.show(`
      <h1>AFTER ACTION</h1>
      <p class="lede">${w.diff.name} &middot; ${w.diff.rank} &middot; seed ${w.seed}</p>
      <div class="gradebox ${r.pass ? '' : 'fail'}">${r.grade} &nbsp;<span style="font-size:16px;letter-spacing:2px">${r.gradeName}</span></div>
      <div class="result">
        <span>Saved</span><span>${r.saved} of ${r.total} (${pct(r.savedFrac)})</span>
        <span>Driven out by road</span><span>${Math.max(0, r.saved - w.stats.rescued - r.stoodDown)}</span>
        <span>Flown out</span><span>${w.stats.rescued}</span>
        <span>Never had to leave</span><span>${r.stoodDown}</span>
        <span>Lost</span><span>${r.lost}</span>
        <span>Buildings standing</span><span>${r.housesSaved} of ${r.housesTotal}</span>
        <span>Burnt</span><span>${r.hectares.toFixed(1)} ha (${pct(r.burntFrac)} of the fuel)</span>
        <span>Duration</span><span>${mmss(r.elapsed)}</span>
        ${r.crewLost > 0 ? `<span class="bad">Firefighters lost</span><span class="bad">${r.crewLost}</span>` : ''}
        <span>Score</span><span>${r.score}</span>
      </div>
      <p>${commentary}</p>
      <h1 style="font-size:16px;letter-spacing:2px;margin:18px 0 8px;color:var(--cyan)">NEXT TIME</h1>
      <ul class="tips">${r.advice.map((a) => `<li>${esc(a)}</li>`).join('')}</ul>
      <button class="btn" id="again">SAME INCIDENT AGAIN</button>
      <button class="btn" id="tomenu">INCIDENT LIST</button>
      ${
        // The incident is over and nothing is in progress, so this is the one
        // moment a link out costs the player nothing. Hidden while there is
        // only one game, rather than shown as a button that goes nowhere new.
        SHOW_SITE_LINKS && GAMES.length > 1
          ? `<a class="btn" href="${SITE.url}" target="_blank" rel="noopener">MORE GAMES</a>`
          : ''
      }
      ${this.siteFooter()}
    `);
    card.querySelector('#again')!.addEventListener('click', onAgain);
    card.querySelector('#tomenu')!.addEventListener('click', onMenu);
  }
}

/** Exposed so a selected engine can be highlighted from outside. */
export type { Engine };


